import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import ImageUploader from '../components/ImageUploader';

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    filterKey: '',
    image: '',
    type: 'PRODUCT',
    description: ''
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/admin/categories');
      setCategories(response.data.data || response.data || []);
      setLoading(false);
    } catch (err) {
      console.error("Failed to fetch categories", err);
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (name === 'name' && !editMode) {
      setFormData(prev => ({ ...prev, filterKey: value.toLowerCase().replace(/\s+/g, '_') }));
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.filterKey) {
      alert("Name and Filter Key are required.");
      return;
    }

    try {
      if (editMode) {
        await api.put(`/admin/categories/${selectedId}`, formData);
        alert("✅ Category updated successfully!");
      } else {
        await api.post(`/admin/categories`, formData);
        alert("✅ New category created!");
      }
      fetchCategories();
      setShowModal(false);
      resetForm();
    } catch (err) {
      console.error("Save failed", err);
      alert("❌ Error saving category.");
    }
  };

  const handleEdit = (cat) => {
    setEditMode(true);
    setSelectedId(cat._id);
    setFormData({
      name: cat.name,
      filterKey: cat.filterKey,
      image: cat.image || '',
      type: cat.type || 'PRODUCT',
      description: cat.description || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure? This will affect all associated products/centres.")) return;
    try {
      await api.delete(`/admin/categories/${id}`);
      alert("✅ Category deleted!");
      fetchCategories();
    } catch (err) {
      console.error("Delete failed", err);
    }
  };

  const resetForm = () => {
    setEditMode(false);
    setSelectedId(null);
    setFormData({
      name: '',
      filterKey: '',
      image: '',
      type: 'PRODUCT',
      description: ''
    });
  };

  return (
    <div className="p-8 bg-[#FAF9F6] min-h-screen">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-serif font-bold text-[#2A2858]">Global Categories</h1>
          <p className="text-stone-500 mt-1 text-sm">Organize Products, Services, and Centres dynamically</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="bg-[#2A2858] text-white px-5 py-2.5 rounded-xl font-bold text-sm shadow-lg hover:bg-[#14122B] transition-all flex items-center gap-2"
        >
          <span className="material-symbols-outlined text-sm font-bold">add_box</span>
          Create Category
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64 animate-pulse">
           <div className="text-[#2A2858] font-serif text-xl">Divine arrangement in progress...</div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-stone-100 shadow-xl overflow-hidden">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-stone-50 border-b border-stone-100 text-[10px] uppercase tracking-widest text-[#2A2858] font-bold">
                <th className="p-6">Visual</th>
                <th className="p-6">Category Info</th>
                <th className="p-6">Type</th>
                <th className="p-6">Slug/Key</th>
                <th className="p-6 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-50">
              {categories.map((cat) => (
                <tr key={cat._id} className="hover:bg-stone-50 transition-colors">
                  <td className="p-6">
                    <div className="w-12 h-12 rounded-xl overflow-hidden bg-stone-100 border border-stone-200">
                      {cat.image ? (
                        <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-stone-300">
                           <span className="material-symbols-outlined">category</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="p-6">
                    <div className="font-bold text-stone-800">{cat.name}</div>
                    <div className="text-[10px] text-stone-400 line-clamp-1">{cat.description || 'No description'}</div>
                  </td>
                  <td className="p-6">
                    <span className={`px-2 py-1 rounded text-[10px] font-bold ${
                      cat.type === 'PRODUCT' ? 'bg-blue-50 text-blue-600' : 
                      cat.type === 'SERVICE' ? 'bg-orange-50 text-orange-600' : 'bg-green-50 text-green-600'
                    }`}>
                      {cat.type}
                    </span>
                  </td>
                  <td className="p-6 text-stone-500 font-mono text-[10px]">{cat.filterKey}</td>
                  <td className="p-6">
                    <div className="flex justify-center gap-3">
                      <button onClick={() => handleEdit(cat)} className="text-stone-400 hover:text-blue-500 transition-colors">
                        <span className="material-symbols-outlined text-xl">edit</span>
                      </button>
                      <button onClick={() => handleDelete(cat._id)} className="text-stone-400 hover:text-red-500 transition-colors">
                        <span className="material-symbols-outlined text-xl">delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {categories.length === 0 && (
                <tr>
                  <td colSpan="5" className="p-12 text-center text-stone-400 font-serif">No dynamic categories found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal Form */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300 max-h-[90vh] flex flex-col">
            <div className="bg-[#FAF9F6] border-b border-stone-100 px-8 py-5 flex justify-between items-center shrink-0">
              <h3 className="font-serif text-2xl font-bold text-[#2A2858]">
                {editMode ? 'Edit Category' : 'New Dynamic Category'}
              </h3>
              <button onClick={() => setShowModal(false)} className="text-stone-400 hover:text-red-500 transition-colors">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            
            <form className="p-8 space-y-5 overflow-y-auto flex-1 custom-scrollbar">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-xs font-bold text-stone-500 uppercase mb-2">Icon/Image (Cloudinary)</label>
                  <div className="flex flex-col gap-4 bg-stone-50 p-6 rounded-2xl border border-stone-100">
                    {formData.image ? (
                      <div className="w-24 h-24 rounded-xl overflow-hidden border-2 border-[#2A2858]/20 shadow-md relative group shrink-0 mx-auto md:mx-0">
                        <img src={formData.image} alt="Preview" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <span className="text-white text-[8px] font-black uppercase tracking-widest text-center">Current<br/>Icon</span>
                        </div>
                      </div>
                    ) : (
                      <div className="w-24 h-24 rounded-xl border-2 border-dashed border-stone-200 flex flex-col items-center justify-center bg-white shrink-0 mx-auto md:mx-0">
                        <span className="material-symbols-outlined text-stone-200 text-3xl">add_a_photo</span>
                      </div>
                    )}
                    
                    <div className="flex-1 space-y-3">
                      <ImageUploader 
                        buttonText={formData.image ? "Change Icon" : "Upload Sacred Icon"}
                        onUploadSuccess={(url) => setFormData({...formData, image: url})}
                        folder="categories"
                        oldUrl={formData.image}
                      />
                      <p className="text-[10px] text-stone-400 italic">Cloudinary hosting ensures fast loading across all devices.</p>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase mb-2">Name</label>
                  <input type="text" name="name" value={formData.name} onChange={handleInputChange} className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:border-[#2A2858] outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase mb-2">Type</label>
                  <select name="type" value={formData.type} onChange={handleInputChange} className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:border-[#2A2858] outline-none bg-white">
                    <option value="PRODUCT">Product</option>
                    <option value="SERVICE">Service (Ceremony)</option>
                    <option value="CENTRE">Centre Group</option>
                    <option value="ALL">All Purpose</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-stone-500 uppercase mb-2">Filter Key (Slug)</label>
                <input type="text" name="filterKey" value={formData.filterKey} onChange={handleInputChange} className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:border-[#2A2858] outline-none" />
              </div>

              <div>
                <label className="block text-xs font-bold text-stone-500 uppercase mb-2">Description</label>
                <textarea name="description" value={formData.description} onChange={handleInputChange} rows="2" className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:border-[#2A2858] outline-none resize-none"></textarea>
              </div>

              <div className="pt-4 flex gap-3">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 bg-stone-100 text-stone-500 font-bold py-3.5 rounded-xl hover:bg-stone-200 transition-all">Discard</button>
                <button type="button" onClick={handleSave} className="flex-1 bg-[#2A2858] text-white font-bold py-3.5 rounded-xl hover:bg-[#14122B] transition-all shadow-lg active:scale-95">
                  {editMode ? 'Update Category' : 'Create Category'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Categories;